<template>
  <content-dialog
    :visible.sync="modalVisible"
    :fullscreen="$vuetify.breakpoint.xsOnly"
    :small-title="$vuetify.breakpoint.xsOnly"
    max-width="500px"
  >
    <!--<editor-fold desc="Dialog Title">-->
    <template slot="dialogTitle">
      <span v-if="inventoryItemDirty.id">
        Edit Inventory Item
      </span>
      <span v-else>
        Add New Inventory Item
      </span>
    </template>
    <!--</editor-fold>-->

    <!--<editor-fold desc="Dialog Content">-->
    <v-card-text :class="{'pa-4': $vuetify.breakpoint.smAndUp }">
      <v-text-field
        v-model="inventoryItemDirty.label"
        v-validate="'required'"
        data-vv-as="label"
        name="label"
        :error-messages="veeErrors.collect('label')"
        label="Label"
        autocomplete="disable"
        outlined
        autofocus
      />

      <v-text-field
        v-model="inventoryItemDirty.code"
        data-vv-as="code"
        name="item_code"
        :error-messages="veeErrors.collect('item_code')"
        label="Item Code"
        hint="Barcode or other unique identification"
        autocomplete="disable"
        outlined
      />

      <v-row>
        <v-col
          cols="8"
        >
          <v-text-field
            v-model="inventoryItemDirty.stock"
            v-validate="'required|decimal'"
            data-vv-as="current stock"
            name="stock"
            :error-messages="veeErrors.collect('stock')"
            label="Current Stock"
            :disabled="!!inventoryItem.id"
            autocomplete="disable"
            outlined
          />
        </v-col>
        <v-col cols="8">
          <v-text-field
            v-model="inventoryItemDirty.unit"
            v-validate="'required'"
            data-vv-as="unit"
            name="unit"
            :error-messages="veeErrors.collect('unit')"
            label="Unit"
            placeholder="Enter unit"
            hint="e.g. pcs / kg / cm / m3 / ..."
            autocomplete="disable"
            outlined
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col
          cols="6"
          class="mr-4"
        >
          <currency-input
            v-model="inventoryItemDirty.unit_price"
            v-validate="'required|decimal'"
            data-vv-as="unit price"
            name="unit_price"
            :error-messages="veeErrors.collect('unit_price')"
            label="Unit Price"
            autocomplete="disable"
            outlined
          />
        </v-col>
        <v-col
          cols="5"
        >
          <v-select
            v-model="inventoryItemDirty.currency"
            v-validate="'required'"
            label="Currency"
            data-vv-as="currency"
            name="currency"
            :error-messages="veeErrors.collect('currency')"
            outlined
            single-line
            :items="currencies"
          />
        </v-col>
        <!--        <v-col-->
        <!--          cols="5"-->
        <!--        >-->
        <!--          <currency-input-->
        <!--            v-model="margin"-->
        <!--            v-validate="'required|decimal'"-->
        <!--            data-vv-as="margin"-->
        <!--            name="margin"-->
        <!--            :error-messages="veeErrors.collect('margin')"-->
        <!--            label="Margin"-->
        <!--            suffix="%"-->
        <!--            outlined-->
        <!--          />-->
        <!--        </v-col>-->
      </v-row>

      <v-subheader>
        Supplier
      </v-subheader>

      <v-autocomplete
        v-model="inventoryItemDirty.contractor_id"
        outlined
        label="Select supplier"
        clearable
        :items="contractors.data"
        item-text="name"
        item-value="id"
        :loading="contractors.loading"
      />

      <v-subheader>
        Available In
      </v-subheader>

      <v-select
        v-model="inventoryItemDirty.country_id"
        v-validate="'required|numeric'"
        label="Country"
        data-vv-as="country"
        name="country"
        :error-messages="veeErrors.collect('country')"
        item-value="id"
        item-text="name"
        outlined
        single-line
        clearable
        :items="allowedCountries"
      >
        <template v-slot:item="{ item }">
          <flag
            v-if="item.code"
            :iso="item.code"
            class="mr-2"
            :squared="false"
          />
          {{ item.name }}
        </template>
        <template v-slot:selection="{ item }">
          <flag
            v-if="item.code"
            :iso="item.code"
            class="mr-2"
            :squared="false"
          />
          {{ item.name }}
        </template>
      </v-select>

      <v-select
        v-model="inventoryItemDirty.base_id"
        v-validate="'numeric'"
        item-value="id"
        item-text="name"
        label="Base (optional)"
        hint="Select if the item is only available within a base"
        data-vv-as="base"
        name="base"
        :error-messages="veeErrors.collect('base')"
        outlined
        single-line
        clearable
        :items="allowedBases"
      />

      <v-subheader>
        Additonal Description
      </v-subheader>

      <v-textarea
        v-model="inventoryItemDirty.description"
        outlined
        label="Description"
      />
    </v-card-text>
    <!--</editor-fold>-->

    <!--<editor-fold desc="Dialog Actions">-->
    <template slot="dialogActions">
      <v-btn
        v-if="inventoryItemDirty.id && $can('delete', 'InventoryItem')"
        small
        outlined
        color="error"
        @click="toggleConfirmationDialog(true)"
      >
        <v-icon
          left
          small
        >
          delete
        </v-icon>
        Delete
      </v-btn>
      <v-spacer />
      <v-btn
        text
        color="grey"
        @click="modalVisible = false"
      >
        Cancel
      </v-btn>
      <v-btn
        color="primary"
        :loading="submitting"
        @click="submit"
      >
        <v-icon
          small
          left
        >
          check
        </v-icon>
        Save
      </v-btn>
    </template>
    <!--</editor-fold>-->

    <confirm-delete-dialog
      :visible.sync="deleteConfirmationDialogVisible"
      :submitting="deleting"
      text="Are you sure you want to delete this inventory item?"
      @confirmed="deleteInventoryItem()"
    />
  </content-dialog>
</template>

<script>
  import cloneDeep from 'lodash/cloneDeep'
  import ContentDialog from '@/components/helpers/Dialog'
  import InventoryItem from '@/models/InventoryItem'
  import validationScrollMixin from '@/mixins/validationScrollMixin'
  import CurrencyInput from '@/components/helpers/CurrencyInput'
  import CurrencyExchangeRate from '@/models/CurrencyExchangeRate'
  import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
  import ConfirmDeleteDialog from '@/components/helpers/ConfirmDeleteDialog'
  import Contractor from '@/models/Contractor'

  export default {
    name: 'InventoryItemEditDialog',
    components: {
      ConfirmDeleteDialog,
      CurrencyInput,
      ContentDialog
    },
    mixins: [
      validationScrollMixin, deleteConfirmationMixin
    ],
    props: {
      visible: Boolean,
      inventoryItem: {
        type: Object,
        default: () => InventoryItem.emptyModel
      }
    },
    data() {
      return {
        inventoryItemDirty: cloneDeep(this.inventoryItem),
        submitting: false
      }
    },
    computed: {
      modalVisible: {
        get: function () {
          return this.visible
        },
        set: function (value) {
          this.$emit('update:visible', value)
        }
      },
      margin: {
        get: function () {
          if (!this.inventoryItemDirty.navigare_margin) return null

          return (this.inventoryItemDirty.navigare_margin * 100).toFixed(2)
        },
        set: function (value) {
          this.inventoryItemDirty.navigare_margin = (value / 100).toFixed(4)
        }
      },
      currentCountry() {
        return this.$store.state.service.currentLocation.country
      },
      currentBase() {
        return this.$store.state.service.currentLocation.base
      },
      allowedBases() {
        return this.$store.state.auth.user.service_allow_bases.filter(b => b.country_id === this.inventoryItemDirty.country_id)
      },
      allowedCountries() {
        return this.$store.state.auth.user.service_allow_countries
      },
      contractors() {
        return {
          loading: this.$store.state.service.contractors.loading,
          data: this.$store.state.service.contractors.data.filter(c => c.type === Contractor.TYPE_SUPPLIER)
        }
      }
    },
    watch: {
      inventoryItem: function (value) {
        this.inventoryItemDirty = cloneDeep(value)
      }
    },
    created() {
      this.currencies = CurrencyExchangeRate.currencies

      if (!this.inventoryItemDirty.id && this.currentCountry) {
        this.inventoryItemDirty.country_id = this.currentCountry.id
        this.inventoryItemDirty.currency = this.currentCountry.currency || 'EUR'
      }

      if (!this.inventoryItemDirty.id && this.currentBase) {
        this.inventoryItemDirty.base_id = this.currentBase.id
      }
    },
    methods: {
      async submit() {
        let validation = await this.$validator.validateAll()

        if (!validation) {
          this.showAlertAndScrollToFirstError()
          return
        }

        this.saveInventoryItem()
      },
      async saveInventoryItem() {
        this.submitting = true

        const data = {
          base_id: this.inventoryItemDirty.base_id,
          code: this.inventoryItemDirty.code,
          contractor_id: this.inventoryItemDirty.contractor_id,
          country_id: this.inventoryItemDirty.country_id,
          currency: this.inventoryItemDirty.currency,
          description: this.inventoryItemDirty.description,
          label: this.inventoryItemDirty.label,
          navigare_margin: this.inventoryItemDirty.navigare_margin,
          stock: this.inventoryItemDirty.stock,
          unit: this.inventoryItemDirty.unit,
          unit_price: this.inventoryItemDirty.unit_price
        }

        try {
          const savedInventoryItem = await InventoryItem.api.save(data, this.inventoryItemDirty.id)

          this.$store.dispatch('alert', {
            'text': 'Inventory item successfully saved',
            'color': 'success'
          })

          this.$emit(this.inventoryItemDirty.id ? 'inventoryItem:updated' : 'inventoryItem:created', savedInventoryItem)
          this.inventoryItemDirty = cloneDeep(InventoryItem.emptyModel)

          this.$validator.reset()

          this.modalVisible = false
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
          throw e
        } finally {
          this.submitting = false
        }
      },
      async deleteInventoryItem() {
        this.deleting = true

        try {
          await InventoryItem.api.destroy(this.inventoryItemDirty.id)

          this.$store.dispatch('alert', {
            'text': 'Inventory item deleted',
            'color': 'info'
          })
          this.modalVisible = false
          this.$emit('inventoryItem:deleted', this.inventoryItemDirty.id)
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
          throw e
        } finally {
          this.deleteConfirmationDialogVisible = false
          this.deleting = false
        }
      }
    }
  }
</script>
